import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, of, throwError } from 'rxjs';
import { catchError, filter, map, retry, shareReplay, switchMap, tap } from 'rxjs/operators';
import { SECRETS_SERVICE, ADMIN_SERVICE } from './service-url'
import { apimUrlConfig, avatarAppConfig, speechAppConfig } from '../config/config';
import { skipSpinnerOptions } from '../v2/core/services/http-options.utils';

// Union of every secret the app needs, so one request at bootstrap serves all
// consumers. speechAppConfig and avatarAppConfig overlap, hence the de-dupe.
const ALL_SECRET_NAMES: string[] = [...new Set([
  apimUrlConfig.apimUrl,
  speechAppConfig.speechSubKey,
  speechAppConfig.speechEndpoint,
  avatarAppConfig.endPoint,
  avatarAppConfig.cogSvcSubKey,
  avatarAppConfig.iceEndPoint,
  avatarAppConfig.iceKey,
])];

@Injectable({
  providedIn: 'root'
})
export class KeyVaultService {
  // The vault-name mapping for a business unit is static reference data, but
  // ten components resolve it independently on mount, so a page load fired
  // the same GET repeatedly. One shared, replayed request per product code
  // serves all subscribers; a failed request is evicted so it can retry.
  private readonly vaultNameCache = new Map<string, Observable<any>>();

  // Secret values are static for the lifetime of a session, but a dozen
  // components request them independently on mount. Cached per vault +
  // secret-name set; a failed request is evicted so it can retry.
  private readonly secretsCache = new Map<string, Observable<any>>();

  private readonly secretsRequests = new Map<string, Observable<Record<string, string>>>();
  private readonly secretsSubject = new BehaviorSubject<Record<string, string> | null>(null);

  /** Resolved secret values, keyed by secret name. Emits once loaded and replays to late subscribers. */
  readonly secrets$: Observable<Record<string, string>> = this.secretsSubject.asObservable().pipe(
    filter((secrets): secrets is Record<string, string> => !!secrets)
  );

  /** Resolved APIM base URL. */
  readonly apimUrl$: Observable<string> = this.secrets$.pipe(
    map(secrets => secrets[apimUrlConfig.apimUrl] ?? ''),
    filter(url => !!url)
  );

  constructor(private readonly http: HttpClient) { }

  /** Last resolved APIM base URL, or '' if the bootstrap has not completed yet. */
  get apimUrl(): string {
    return this.secretsSubject.value?.[apimUrlConfig.apimUrl] ?? '';
  }

  /**
   * Fetches every secret in one request, once per business unit, and publishes
   * them on secrets$. Bootstrapped from AppComponent; everywhere else should
   * read secrets$ / apimUrl$ instead of calling this.
   */
  loadSecrets(): Observable<Record<string, string>> {
    const productCode = (sessionStorage.getItem('businessUnitCode') || '').toLowerCase();
    if (!productCode) {
      return of({});
    }
    const cached = this.secretsRequests.get(productCode);
    if (cached) {
      return cached;
    }
    const request$ = this.getVaultNameByCode().pipe(
      switchMap(vaultData => this.getSecret(ALL_SECRET_NAMES, vaultData)),
      map(data => data?.secrets ?? {}),
      // One extra attempt covers a transient vault/network blip without a retry storm.
      retry({ count: 1, delay: 1000 }),
      tap(secrets => this.secretsSubject.next(secrets)),
      catchError(err => {
        this.secretsRequests.delete(productCode);
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.secretsRequests.set(productCode, request$);
    return request$;
  }

  getSecret(key: string[], vaultData): Observable<any> {
    const vaultName = vaultData?.vault_name;
    const cacheKey = `${vaultName}|${[...key].sort().join(',')}`;
    const cached = this.secretsCache.get(cacheKey);
    if (cached) {
      return cached;
    }
    const payload = { "secret_names": key, "vault_name": vaultName };
    const request$ = this.http.post<any>(SECRETS_SERVICE.getKeyVault, payload, { ...skipSpinnerOptions() }).pipe(
      catchError(err => {
        this.secretsCache.delete(cacheKey);
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.secretsCache.set(cacheKey, request$);
    return request$;
  }

  getVaultNameByCode(){
    const productCode = sessionStorage.getItem('businessUnitCode').toLowerCase();
    const cached = this.vaultNameCache.get(productCode);
    if (cached) {
      return cached;
    }
    const request$ = this.http.get<any>(ADMIN_SERVICE.getKeyVaultByCode+ productCode, { ...skipSpinnerOptions() }).pipe(
      catchError(err => {
        this.vaultNameCache.delete(productCode);
        return throwError(() => err);
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
    this.vaultNameCache.set(productCode, request$);
    return request$;
  }
}
